import Alpine from "alpinejs";
import { nanoid } from "nanoid";

export enum FlashType {
  NOTICE = "notice",
  ALERT = "alert",
  SUCCESS = "success",
  ERROR = "error",
}

interface RawFlashMessage {
  type: FlashType;
  text: string;
}

interface FlashMessage {
  id: string;
  type: FlashType;
  text: string;
}

interface FlashPushOptions {
  timeout?: number;
}

interface FlashStore {
  messages: FlashMessage[];

  push(
    type: FlashType,
    text: string,
    opts?: FlashPushOptions,
  ): string | undefined;

  remove(id: string): void;
}

Alpine.store("flash", {
  messages: [] as FlashMessage[],

  push(type, text, { timeout = 10000 } = {}) {
    if (!text) return;

    const id = nanoid();
    this.messages.push({ id, type, text });

    if (timeout) {
      setTimeout(() => this.remove(id), timeout);
    }

    return id;
  },

  remove(id) {
    this.messages = this.messages.filter((m) => m.id !== id);
  },
} satisfies FlashStore);

export function parseRenderedFlashData() {
  const renderedData = document.getElementById("flash-data")?.textContent;
  return parseFlashData(renderedData);
}

export function parseFlashData(raw?: string): RawFlashMessage[] {
  if (!raw) {
    return [];
  }

  try {
    return JSON.parse(raw) as RawFlashMessage[];
  } catch (e) {
    console.log("Failed to parse flash data", e, raw);
    return [];
  }
}

export function propagateFlashesToParent(data?: RawFlashMessage[]) {
  if (!data) {
    data = parseRenderedFlashData();
  }

  if (window.parent !== window) {
    data.forEach((f) => {
      if (window.parent && window.parent.Flash) {
        window.parent.Flash.push(f.type, f.text);
      }
    });
  }
}

export default () => ({
  init() {
    const store = Alpine.store("flash") as FlashStore;

    const initial = parseRenderedFlashData();
    initial.forEach(({ type, text }) => {
      store.push(type, text);
    });
  },
});

declare global {
  interface Window {
    Flash: {
      store(): FlashStore;
      push(
        type: FlashType,
        text: string,
        opts?: FlashPushOptions,
      ): string | undefined;
      remove(id: string): void;
    };
  }
}

window.Flash = {
  store() {
    return Alpine.store("flash") as FlashStore;
  },

  push(type, text, opts) {
    return this.store().push(type, text, opts);
  },

  remove(id) {
    this.store().remove(id);
  },
};
